import { ChevronLeft, Code, ImageIcon, Megaphone, Type } from "lucide-react";
import type { ReactNode } from "react";

import type { ComponentType } from "@intelligems/ig-types";
import { useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { Panel } from "@src/features/experienceBuilder/modifications/common/panel/Panel";
import { useDispatch } from "@src/store";
import { ExperienceBuilderAction } from "@src/store/slices/experienceBuilder";

interface ComponentOption {
  type: ComponentType;
  label: string;
  icon: ReactNode;
  illustration: string;
}

const componentOptions: ComponentOption[] = [
  {
    type: "bannerComponent",
    label: "Banner",
    icon: <Megaphone size={16} />,
    illustration: "/illustrations/component-banner.svg",
  },
  {
    type: "textComponent",
    label: "Text Block",
    icon: <Type size={16} />,
    illustration: "/illustrations/component-text-block.svg",
  },
  {
    type: "imageComponent",
    label: "Image",
    icon: <ImageIcon size={16} />,
    illustration: "/illustrations/component-image.svg",
  },
  {
    type: "genericComponent",
    label: "Generic",
    icon: <Code size={16} />,
    illustration: "/illustrations/component-text-block.svg",
  },
];

export const ComponentSelectionPanel = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  return (
    <Stack sx={{ height: "100%", backgroundColor: "white" }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        onClick={() => dispatch(ExperienceBuilderAction.component.clearDraft())}
        sx={{
          px: 3,
          height: 64,
          cursor: "pointer",
          borderBottom: `1px solid ${theme.igBorders.core.default}`,
          backgroundColor: "white",
        }}
      >
        <ChevronLeft size={16} />
        <Panel.Header>Choose component</Panel.Header>
      </Stack>

      <Grid container spacing={2} sx={{ p: 3, overflow: "auto" }}>
        {componentOptions.map(({ type, label, icon, illustration }) => (
          <Grid key={type} size={6}>
            <Stack
              onClick={() =>
                void dispatch(
                  ExperienceBuilderAction.component.selectType(type)
                )
              }
              sx={{
                borderRadius: 2,
                border: `1px solid ${theme.igBorders.core.default}`,
                cursor: "pointer",
                overflow: "hidden",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              {/* Preview illustration */}
              <Box
                sx={{
                  height: 100,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 2,
                }}
              >
                <img
                  src={illustration}
                  alt={label}
                  style={{ width: "80%", height: "auto" }}
                />
              </Box>

              {/* Label */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ px: 2, py: 1.5 }}
              >
                <Box sx={{ color: "text.secondary", display: "flex" }}>
                  {icon}
                </Box>
                <Panel.Header>{label}</Panel.Header>
              </Stack>
            </Stack>
          </Grid>
        ))}
      </Grid>

      <Panel.Buttons>
        <Panel.CancelButton
          onClick={() =>
            dispatch(ExperienceBuilderAction.component.clearDraft())
          }
        />
      </Panel.Buttons>
    </Stack>
  );
};
